import { Button } from "@/components/ui/baseUi/button"
import { Text } from "@/components/ui/baseUi/text"
import { cn } from "@/modules/lib/utils"
import { useCommentFormik } from "@/modules/scores/scoreForms/hooks/useCommentFormFormik"
import type { ProgramInfiniteSessionEntity } from "@/modules/sessions/entities/programInfiniteSessionEntity"
import type { ProgramOnOffSessionEntity } from "@/modules/sessions/entities/programOnOffSessionEntity"
import type { SessionBlockExerciseEntity } from "@/modules/sessions/entities/sessionBlockExerciseEntity"
import { word } from "@modules/core/utils/i18n"
import { MediaUpload } from "@modules/ui/components/fileUpload/mediaUpload"
import { useConfirmPopup } from "@modules/ui/components/popup/huConfirmPopup"
import { useImperativeHandle } from "react"

export type CommentFormRef = {
  onClose: () => Promise<boolean>
}

type CommentFormProps = {
  exercise: SessionBlockExerciseEntity
  parentSession: ProgramInfiniteSessionEntity | ProgramOnOffSessionEntity
  className?: string
  ref?: React.Ref<CommentFormRef>
} & Omit<React.ComponentProps<"form">, "ref">

export function CommentForm({ exercise, parentSession, className, ref, ...props }: CommentFormProps) {
  const { commentFormik: form } = useCommentFormik(parentSession, exercise)
  const confirmPopup = useConfirmPopup()

  useImperativeHandle(ref, () => ({
    onClose: async () => {
      if (form.state.isPristine) return true

      return await new Promise<boolean>((resolve) => {
        confirmPopup.show({
          title: word("session.comment.form.closeMessage.title"),
          message: word("session.comment.form.closeMessage.message"),
          accept: () => {
            resolve(true)
          },
          cancel: () => {
            resolve(false)
          },
          footerProps: {
            confirmText: word("global.ok"),
          },
        })
      })
    },
  }))

  const isUploadingAMedia = form.state.values.medias.some((media) => media.isUploading)

  return (
    <form
      className={cn("flex flex-col gap-5 px-8 pt-18 pb-12", className)}
      onSubmit={(e) => {
        e.preventDefault()
        void form.handleSubmit()
      }}
      {...props}
    >
      {/* Comment form title*/}
      <div className="flex flex-col gap-2">
        <Text fontStyle="TL">{word("session.comment.form.title")}</Text>
        <Text fontStyle="BS">{word("session.comment.form.subtitle")}</Text>
      </div>

      {/* Comment input */}
      <form.AppField name="note">
        {(field) => (
          <field.Field>
            <field.Label required>{word("session.scoreboard.card.comment")}</field.Label>
            <field.Content>
              <field.Textarea
                placeholder={word("exercise.score.addComment")}
                className="field-sizing-fixed h-35 bg-surface-background"
                rows={5}
              />
              <field.Message />
            </field.Content>
          </field.Field>
        )}
      </form.AppField>

      {/* Comment visibility switch */}
      <div className="gap-4 px-2">
        <Text className="my-1 uppercase" fontStyle="LS">
          {word("session.comment.visibility.label")}
        </Text>
        <form.AppField name="noteAndMediaIsVisible">
          {(field) => (
            <div className="mt-2 flex w-full flex-row items-center justify-between gap-2">
              <Text fontStyle="BS" className="flex-1">
                {word("session.comment.visibility.description")}
              </Text>
              <field.Switch
                checked={field.state.value}
                onCheckedChange={(checked) => {
                  field.handleChange(checked)
                }}
              />
            </div>
          )}
        </form.AppField>
      </div>

      {/* Video upload */}
      <form.AppField name="medias">
        {(field) => (
          <>
            <MediaUpload
              className="my-2"
              type="mediaItem"
              mediaList={field.state.value}
              updateMediaList={(mediaList) => {
                field.handleChange(mediaList)
              }}
              updateMediaItem={(mediaItem, index) => {
                const updated = [...field.state.value]
                updated[index] = mediaItem
                field.handleChange(updated)
              }}
            />
            <field.Message />
          </>
        )}
      </form.AppField>

      {/* Save button */}
      <form.Subscribe selector={(s) => [s.isSubmitting, s.canSubmit]}>
        {([isSubmitting, canSubmit]) => (
          <Button
            variant="primary"
            type="submit"
            size="lg"
            disabled={isUploadingAMedia || !canSubmit || isSubmitting}
            isLoading={isSubmitting}
          >
            {word("global.save")}
          </Button>
        )}
      </form.Subscribe>
    </form>
  )
}
